import { isKPIChartEnhancementsEnabled } from '@thoughtspot/session-service';
import { dequeuePrimaryRenderRequest, enqueuePrimaryRenderRequest, renderComplete as notifySparklineRenderComplete, } from '@thoughtspot/viz-render-queue';
import cx from 'classnames';
import { uniqueId } from 'lodash';
import React, { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useState, } from 'react';
import styles from '../../../styles/kpi.module.scss';
import { AnswerMode } from '../../chart-viz/chart-viz.util';
import { Analyze } from './analyze';
import { CCAErrorModal } from './cca-error-modal';
import { logger, TEST_IDS } from './constants';
import { SimpleKPIContent } from './simple-kpi-content';
import { useChangeAnalysis } from './use-change-analysis';
import { useKpiModel } from './use-kpi-model';
import { callPerfLog } from '../../../utils/perf-utils';
export const SparklineKPIChart = React.forwardRef((props, ref) => {
    var _a, _b, _c, _d;
    const { chartModel, env, config, chartObj, dataModel, eventsMap, highchartRef, onRenderComplete, } = props;
    const sparklineContainerRef = React.useRef(null);
    const sparklineRef = React.useRef(null);
    const [sparklineQueueKey] = useState(() => uniqueId(`${chartModel.id}-sparkline-`));
    const model = useKpiModel(chartModel);
    const { shouldShowNoDataFound } = model;
    const analyzeButton = useChangeAnalysis(model, {
        chartModel,
        env,
        config,
        chartObj,
        dataModel,
        eventsMap,
    });
    const isKPIWithAnomalyV2Enabled = (_a = env === null || env === void 0 ? void 0 : env.flags) === null || _a === void 0 ? void 0 : _a.isKPIWithAnomalyV2Enabled;
    const hideAnalyzeButton = (_b = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _b === void 0 ? void 0 : _b.hideAnalyzeButton;
    const isKpiStyleEnhancementEnabled = isKPIChartEnhancementsEnabled();
    const currentAnswerMode = (_c = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _c === void 0 ? void 0 : _c.currentAnswerMode;
    const isNonPinboardOrWatchlistMode = currentAnswerMode !== AnswerMode.PINBOARD_TILE &&
        currentAnswerMode !== AnswerMode.WATCHLIST;
    const isPresentModeOn = (_d = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _d === void 0 ? void 0 : _d.shouldScaleFonts;
    useLayoutEffect(() => {
        callPerfLog('TEXT_RENDER_COMPLETE', 'COMPLETE', { trackingId: chartModel.id });
    }, [chartModel.id]);
    useImperativeHandle(ref, () => ({
        setSize: (_chartWidth, _chartHeight, _flag) => {
            if (sparklineContainerRef.current && sparklineRef.current) {
                const { width, height, } = sparklineContainerRef.current.getBoundingClientRect();
                sparklineRef.current.setSize(width, height - 10, true);
                sparklineRef.current.redraw(true);
            }
        },
        getSelectedPoints: () => sparklineRef.current
            ? sparklineRef.current.getSelectedPoints()
            : [],
    }));
    useEffect(() => {
        if (shouldShowNoDataFound ||
            !sparklineContainerRef.current ||
            !chartModel ||
            !dataModel ||
            !chartObj ||
            !eventsMap ||
            !config ||
            !env) {
            onRenderComplete === null || onRenderComplete === void 0 ? void 0 : onRenderComplete();
            return undefined;
        }
        const perfTrackingId = chartModel.id;
        enqueuePrimaryRenderRequest(sparklineQueueKey, () => {
            callPerfLog('VIZ_RENDER_QUEUE_PRIMARY', 'START', { trackingId: perfTrackingId });
            if (!sparklineContainerRef.current) {
                notifySparklineRenderComplete(sparklineQueueKey);
                onRenderComplete === null || onRenderComplete === void 0 ? void 0 : onRenderComplete();
                return;
            }
            Promise.all([
                import('../../../highcharts/highchart-chart-util'),
                import('../../../reactcharts/secondary-render-util'),
            ])
                .then(([{ chartUIRenderHighchart }, { addAnomalyExpectationBarAndLine },]) => chartUIRenderHighchart(chartObj, sparklineContainerRef.current, { ...chartModel, hideAnalyzeButton }, dataModel.getHighchartDatamodel(), eventsMap, config, env).then(chart => {
                callPerfLog('VIZ_RENDER_QUEUE_PRIMARY', 'COMPLETE', { trackingId: perfTrackingId });
                sparklineRef.current = chart;
                highchartRef.current = chart;
                if (isKPIWithAnomalyV2Enabled) {
                    callPerfLog('VIZ_RENDER_QUEUE_SECONDARY', 'START', { trackingId: perfTrackingId });
                    addAnomalyExpectationBarAndLine(chart, chartModel, dataModel);
                    callPerfLog('VIZ_RENDER_QUEUE_SECONDARY', 'COMPLETE', { trackingId: perfTrackingId });
                }
                notifySparklineRenderComplete(sparklineQueueKey);
                onRenderComplete === null || onRenderComplete === void 0 ? void 0 : onRenderComplete();
                return chart;
            }))
                .catch(err => {
                logger.error(err);
                notifySparklineRenderComplete(sparklineQueueKey);
                callPerfLog('VIZ_RENDER_QUEUE_PRIMARY', 'COMPLETE', { trackingId: perfTrackingId });
                onRenderComplete === null || onRenderComplete === void 0 ? void 0 : onRenderComplete();
            });
        });
        return () => {
            dequeuePrimaryRenderRequest(sparklineQueueKey);
            if (sparklineContainerRef.current) {
                sparklineContainerRef.current.innerHTML = '';
            }
        };
    }, [
        shouldShowNoDataFound,
        isKPIWithAnomalyV2Enabled,
        chartModel,
        dataModel,
        chartObj,
        eventsMap,
        config,
        env,
        hideAnalyzeButton,
        highchartRef,
        sparklineQueueKey,
        onRenderComplete,
    ]);
    const handleCloseModal = useCallback(() => analyzeButton.setIsCCAErrorModalOpen(false), [analyzeButton.setIsCCAErrorModalOpen]);
    return (React.createElement("div", { className: cx(styles.kpi, {
            [styles.kpiNoFlexWrap]: isKpiStyleEnhancementEnabled,
        }), "data-testid": TEST_IDS.KPI },
        React.createElement(SimpleKPIContent, { ...props, model: model, onChangeAnalysisClick: analyzeButton.onChangeAnalysisClick, handleKeyPress: analyzeButton.handleKeyPress, tooltip: analyzeButton.tooltip, isEmptyComparison: analyzeButton.isEmptyComparison, isAnomalyV2Enabled: analyzeButton.isAnomalyV2Enabled }),
        !shouldShowNoDataFound ? (React.createElement("div", { ref: sparklineContainerRef, "data-testid": TEST_IDS.SPARKLINE, className: styles.sparkline, "data-tour-id": "tour_kpi_sparkline" })) : null,
        analyzeButton.showAnalyzeButton &&
            !analyzeButton.isPrintMode ? (React.createElement(Analyze, { tooltip: analyzeButton.tooltip, onChangeAnalysisClick: analyzeButton.onChangeAnalysisClick, isAnalyzeButtonEnabled: analyzeButton.isAnalyzeButtonEnabled, isPresentModeOn: isPresentModeOn, handleKeyPress: analyzeButton.handleKeyPress, shouldApplyMargin: isNonPinboardOrWatchlistMode, isEmptyComparison: analyzeButton.isEmptyComparison, setIsCCAErrorModalOpen: analyzeButton.setIsCCAErrorModalOpen })) : null,
        React.createElement(CCAErrorModal, { isOpen: analyzeButton.isCCAErrorModalOpen, onClose: handleCloseModal })));
});
//# sourceMappingURL=sparkline-kpi-chart.js.map